import {
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconClock,
  IconClockExclamation,
  IconPhoneCall,
  IconPhoneOff,
  IconRefresh,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import starOrange from '@/assets/logos/Luminarium_Star_Orange.svg';
import { CallFeedbackCard } from '@/components/CallFeedbackCard';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { InterviewProgress } from '@/components/InterviewProgress';
import { PresenceOrb } from '@/components/PresenceOrb';
import { SafeMarkdown } from '@/components/SafeMarkdown';
import {
  endReasonFailureMessage,
  isFailedEndReason,
  selectCallFailureMessage,
} from '@/features/interview/call-health';
import {
  AudioDeviceSelector,
  CallHealthBanners,
  CopyCellStrip,
  DetailsConfirmationCard,
  InterviewErrorAlert,
  MicCheckPanel,
  MicLevelIndicator,
} from '@/features/interview/components';
import {
  isMac,
  isMobileDevice,
  isWindows,
  useAudioDevices,
} from '@/hooks/useAudioDevices';
import { useCallHealth } from '@/hooks/useCallHealth';
import { useDemoMode } from '@/hooks/useDemoMode';
import { useInterviewSession } from '@/hooks/useInterviewSession';
import { useMicCheck } from '@/hooks/useMicCheck';
import { useOpenAIRealtimeCall } from '@/hooks/useOpenAIRealtimeCall';
import { useUxCopy } from '@/hooks/useUxCopy';
import { apiFetch } from '@/lib/api-client';
import { InterviewErrorCode } from '@/lib/errors/interview-errors';
import { getSupabaseBrowserClient } from '@/lib/supabase/browser';
import { track as breadcrumb } from '@/lib/telemetry/breadcrumb';
import { logger } from '@/utils/logger';
import {
  JOURNEY_STEPS,
  type JourneyStep,
  useJourneyStep,
} from '@/views/journey';

// Top-level interview component — hosts the call lifecycle, demo
// mode, mic permission, and completion flow. Splitting it drives
// state into prop-drilled children. The non-trivial bits (realtime
// call wiring, audio devices) already live in dedicated hooks under
// src/hooks/.
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: top-level interview console
export function InterviewPage() {
  // UI State
  const [detailsConfirmed, setDetailsConfirmed] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<InterviewErrorCode | null>(null);
  const [endedDueToSoftLimit, setEndedDueToSoftLimit] = useState(false);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [callEndSummary, setCallEndSummary] = useState<string | null>(null);
  // LUM-177: call id of the just-ended call, set only when the
  // profile's feedback_capture_enabled flag is on. Non-null renders
  // the floating feedback card; flag off means this never gets set.
  const [feedbackCallId, setFeedbackCallId] = useState<string | null>(null);

  // In-call time warning state (LUM-72). 'none' until T-5, then '5min'
  // until T-1, then '1min' until end.
  const [timeWarning, setTimeWarning] = useState<'none' | '5min' | '1min'>(
    'none',
  );

  // LUM-81: set when the call is running but we could not bind the
  // relay call id to the session (registerCallId exhausted its
  // retries, or start() never returned an id). The interview itself is
  // unaffected — the relay persists the transcript server-side — so
  // this renders as a non-blocking notice, not an error.
  const [registerWarning, setRegisterWarning] = useState(false);

  // Server-owned hard cap (GET /api/realtime/limits, resolved before
  // the call starts). The relay enforces the same cap server-side.
  // Falls back to the env-derived soft limit only before resolution.
  const [hardCapSec, setHardCapSec] = useState<number | null>(null);

  // Refs to track current call/session in callbacks
  const currentCallIdRef = useRef<string | null>(null);
  /** Backs stableThemes: the outline must survive the Start-click
   *  session swap (LUM-185, measured live). */
  const lastThemesRef = useRef<typeof session.themes>([]);
  // Stable session id for telemetry emitted from call event callbacks
  // (LUM-81). Set synchronously from the createSession() result in
  // startInterview — `session.sessionId` React state can lag behind
  // when the call-start event fires right after session creation,
  // which would break the session_created → call_start join.
  const currentSessionIdRef = useRef<string | null>(null);
  const callStartTimeRef = useRef<number | null>(null);
  // Tracks which time-warnings have been emitted on the wire so we
  // never double-fire even if React re-runs the setState updater (LUM-72).
  const emittedWarningsRef = useRef<Set<'5min' | '1min'>>(new Set());
  // Synchronous in-flight guard for startInterview — the button-level
  // disable only kicks in after a React commit, so a fast double-click
  // would otherwise run two `createSession` / limits fetch / startCall
  // chains in parallel (codex flag).
  const startInFlightRef = useRef(false);

  // UX Copy
  const { uxCopy } = useUxCopy();

  // Interview Session Management
  const session = useInterviewSession({
    onAuthError: message => {
      setError(message);
    },
  });

  // Demo Mode
  const demoMode = useDemoMode({
    profile: session.profile,
    sessionId: session.sessionId,
    isComplete: showCompletionMessage,
    initialSummary: callEndSummary,
  });

  // Audio Device Management
  const audioDevices = useAudioDevices({ enabled: detailsConfirmed });
  // Pre-call level check gating Start (LUM-82).
  const micCheck = useMicCheck();

  // OpenAI Realtime over the worker relay (LUM-167). There is no
  // vendor webhook: the relay persists turns server-side, and the
  // browser posts its transcript to /api/realtime/finalize at
  // end-of-call before polling the session row.
  const realtimeCall = useOpenAIRealtimeCall({
    onCallStart: async callId => {
      logger.debug('Call started', { callId });
      setError(null);
      // Reset time-warning state for this call. The metering effect
      // below uses callStartTimeRef + realtimeCall.callActive.
      callStartTimeRef.current = Date.now();
      emittedWarningsRef.current.clear();
      setTimeWarning('none');
      setRegisterWarning(false);
      // A new call supersedes any pending feedback prompt (LUM-177).
      setFeedbackCallId(null);

      // Prefer the synchronously-set ref over React state — state can
      // be stale when call-start fires immediately after createSession.
      const stableSessionId = currentSessionIdRef.current ?? session.sessionId;
      void breadcrumb('call_start', {
        interview_session_id: stableSessionId,
        call_id: callId,
      });

      // LUM-81: bind the call id to the session, and STOP being silent
      // about failure. registerCallId retries internally; if it still
      // fails (or the relay never delivered a call id), let the
      // interview continue but tell the user their history may lag.
      // Breadcrumbs for the failure are emitted inside the hook.
      if (callId) {
        currentCallIdRef.current = callId;
        const registered = await session.registerCallId(callId);
        if (!registered) {
          setRegisterWarning(true);
        }
      } else {
        setRegisterWarning(true);
      }
    },
    // onCallEnd: poll for final session → branch on summary presence
    // → branch on demo mode → analytics dispatch → completion UI.
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: end-of-call orchestration
    onCallEnd: async (endReason, finalize) => {
      logger.debug('Call ended', {
        endReason,
        durationSeconds: finalize.durationSeconds,
        turns: finalize.transcripts.length,
      });

      // LUM-177: pilot testers get a floating feedback prompt for the
      // call that just ended. Gated on the profile flag HERE (not at
      // render) so flag-off profiles never even hold the state. Set
      // BEFORE the awaited finalize + session polling below — the card
      // must appear the moment the call ends, not seconds later when
      // persistence settles (call-11 tester feedback: "felt I was
      // waiting for it").
      if (
        session.profile?.feedback_capture_enabled &&
        currentCallIdRef.current
      ) {
        setFeedbackCallId(currentCallIdRef.current);
      }
      // Enter the wrap-up IMMEDIATELY (LUM-185 flow smoothness): the
      // awaited finalize + session polling below can take seconds, and
      // leaving interviewStarted set through it made inStage show the
      // "connecting…" stage right after the user hung up, then pop to
      // completion. Optimistic: the rare failed-call poll result
      // downgrades to the error view when it lands.
      setInterviewStarted(false);
      setShowCompletionMessage(true);

      // Persist the browser-collected transcript BEFORE polling the
      // session row — /api/realtime/finalize is what populates
      // status/duration (the relay's sideband copy is merged in).
      const finalizeSessionId =
        currentSessionIdRef.current ?? session.sessionId;
      if (finalizeSessionId) {
        try {
          const res = await apiFetch('/api/realtime/finalize', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId: finalizeSessionId,
              callId: currentCallIdRef.current ?? undefined,
              transcript: finalize.transcripts.map(t => ({
                role: t.role,
                text: t.text,
                timestamp: t.timestamp.toISOString(),
              })),
              durationSeconds: finalize.durationSeconds,
              // LUM-187 pass 2 (Codex finding 3): a call that died on
              // the wire is not 'completed' — exhausted resumes and
              // relay failures record honestly as failed.
              status: isFailedEndReason(endReason) ? 'failed' : 'completed',
            }),
          });
          if (!res.ok) {
            logger.error('Failed to persist transcript', {
              status: res.status,
              detail: await res.text().catch(() => ''),
            });
          }
        } catch (err) {
          logger.error('Error posting finalize', { err });
        }
      }

      // Poll for final session data
      const finalSession = await session.updateSessionOnCallEnd(
        currentCallIdRef.current || '',
      );

      // The server-owned cap resolved at start (startCall is gated on
      // it, so it is always set for a call that actually ran).
      const capSec = hardCapSec;

      // LUM-72 time-cap detection. Three signals can mark a call as
      // "time was up, save was successful":
      //   1. The call-end reason reported by the realtime service
      //   2. The session row's stored end_reason
      //   3. The call's duration meeting/exceeding our resolved cap
      // Any one of them is sufficient — we don't want to miss the
      // friendly view because one signal lagged.
      const reasonLower = (
        endReason ??
        finalSession?.end_reason ??
        ''
      ).toLowerCase();
      const reasonIsTimeCap =
        reasonLower.includes('exceeded-max-duration') ||
        reasonLower.includes('max-duration') ||
        reasonLower.includes('time-limit');
      const durationOverLimit =
        capSec !== null &&
        typeof finalSession?.duration === 'number' &&
        finalSession.duration >= capSec;
      // Local-clock backstop: if the end reason is absent (e.g. an
      // error event suppressed onError) AND finalize hasn't populated
      // duration yet, the client's own elapsed time still tells us the
      // call ran past the soft limit.
      const localElapsedOverLimit =
        capSec !== null &&
        callStartTimeRef.current !== null &&
        Date.now() - callStartTimeRef.current >= capSec * 1000;
      const isTimeCap =
        reasonIsTimeCap || durationOverLimit || localElapsedOverLimit;
      // LUM-82: a failure's text follows what the browser observed
      // during the call; the end-reason text is the fallback.
      const callFailed =
        !isTimeCap &&
        (finalSession?.status === 'failed' || isFailedEndReason(endReason));

      if (finalSession) {
        // Bridge summary to demo mode immediately (avoids separate polling)
        if (finalSession.summary) {
          setCallEndSummary(finalSession.summary);
        }

        if (isTimeCap) {
          setEndedDueToSoftLimit(true);
          void breadcrumb('interview_time_capped', {
            ended_reason: reasonLower || null,
            duration_sec: finalSession.duration ?? null,
          });
        }

        // Track call end
        void breadcrumb('call_end', {
          session_id: finalSession.id,
          call_id: currentCallIdRef.current,
          status: finalSession.status,
          end_reason: finalSession.end_reason,
          duration_sec: finalSession.duration,
          soft_limit_hit: durationOverLimit,
        });

        // Show appropriate message
        if (isTimeCap) {
          // Time-cap is a successful termination regardless of the
          // call's stored status. Clear any error the service error
          // event may have set in the race window and render the
          // friendly "your time is up" view.
          setError(null);
          setErrorCode(null);
          setShowCompletionMessage(true);
        } else if (callFailed) {
          setError(
            selectCallFailureMessage(
              callHealth.getObservations(),
              endReasonFailureMessage(finalSession.end_reason),
            ),
          );
          setShowCompletionMessage(false);
        } else {
          setShowCompletionMessage(true);
        }
      } else if (isTimeCap) {
        // No final session yet (persistence lag) but the end reason
        // says time-cap — render the friendly view anyway.
        setEndedDueToSoftLimit(true);
        setError(null);
        setErrorCode(null);
        setShowCompletionMessage(true);
        void breadcrumb('interview_time_capped', {
          ended_reason: reasonLower || null,
          duration_sec: null,
        });
      } else if (callFailed) {
        setError(
          selectCallFailureMessage(
            callHealth.getObservations(),
            endReasonFailureMessage(endReason),
          ),
        );
        setShowCompletionMessage(false);
      } else {
        setShowCompletionMessage(true);
      }

      // LUM-82: a call that completed with the respondent transcribed
      // proves the selected mic — next session defaults to it.
      if (!callFailed && finalize.transcripts.some(t => t.role === 'user')) {
        audioDevices.rememberWorkingMicrophone();
      }

      setInterviewStarted(false);
    },
    onError: (errorData, userMessage) => {
      logger.error('Realtime error', { errorData });
      setError(userMessage);
      void breadcrumb('interview_error', {
        code: errorData.error?.type || 'unknown',
        retry_count: 0,
        phase: 'in_call',
      });
      setShowCompletionMessage(false);
      // The interview never became (or is no longer) live — leave the
      // stage so the error and Start affordances are reachable.
      setInterviewStarted(false);
    },
  });

  // Mid-call banners + what the call ran into (LUM-82).
  const callHealth = useCallHealth({
    callActive: realtimeCall.callActive,
    reconnecting: realtimeCall.reconnecting,
    micSilent: realtimeCall.micSilent,
  });

  // Observatory Split waypoint rail (LUM-155): tell the layout where the
  // interviewee is in the journey. The stage owns the workspace from the
  // moment Start is pressed, so connecting already counts as step 2.
  // The workspace heading mirrors the same step (owner suggestion), so
  // rail and page can never disagree.
  // A LIVE call always keeps the stage (LUM-188 field finding): a
  // mid-call service error (mic hiccup, transient socket) must show
  // as an alert over the call, never dump the user back to pre-call
  // while audio keeps playing underneath.
  const inStage =
    realtimeCall.callActive ||
    (!error && (realtimeCall.connecting || interviewStarted));
  const journeyStep: JourneyStep = !detailsConfirmed
    ? 0
    : inStage
      ? 2
      : showCompletionMessage
        ? 3
        : 1;
  useJourneyStep(journeyStep);

  // In-call elapsed timer — the workspace's focal object during the call.
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    if (!realtimeCall.callActive) return;
    setElapsedSec(0);
    const id = setInterval(() => {
      const start = callStartTimeRef.current;
      if (start) setElapsedSec(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [realtimeCall.callActive]);

  // LUM-187: a tab closed mid-call never runs onCallEnd — the relay's
  // 45s grace eventually closes the row, but the session would never
  // finalize (no summary) and forensics would wait on the stale sweep.
  // Fire a keepalive finalize on pagehide instead. The token is
  // prefetched while the call is active: pagehide gives no time for
  // the async Supabase session read, so the handler must be able to
  // call fetch() synchronously. Empty transcript is safe — the relay's
  // sideband copy is authoritative and the finalize-close nudge
  // flushes it (mergeTranscripts unions the two).
  const authTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!realtimeCall.callActive) return;
    let cancelled = false;
    void getSupabaseBrowserClient()
      .auth.getSession()
      .then(({ data }) => {
        if (!cancelled) {
          authTokenRef.current = data.session?.access_token ?? null;
        }
      });
    const onPageHide = () => {
      const finalizeSessionId =
        currentSessionIdRef.current ?? session.sessionId;
      const token = authTokenRef.current;
      if (!finalizeSessionId || !token) return;
      const started = callStartTimeRef.current;
      void fetch('/api/realtime/finalize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId: finalizeSessionId,
          callId: currentCallIdRef.current ?? undefined,
          transcript: [],
          durationSeconds: started
            ? Math.floor((Date.now() - started) / 1000)
            : 0,
          status: 'completed',
        }),
        keepalive: true,
      });
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      cancelled = true;
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [realtimeCall.callActive, session.sessionId]);

  // Apply devices to the call when active and device selection changes
  useEffect(() => {
    if (realtimeCall.callActive && audioDevices.selectedMicrophone) {
      realtimeCall.setInputDevice(audioDevices.selectedMicrophone);
    }
  }, [realtimeCall.callActive, audioDevices.selectedMicrophone, realtimeCall]);

  useEffect(() => {
    if (realtimeCall.callActive && audioDevices.selectedSpeaker) {
      realtimeCall.setOutputDevice(audioDevices.selectedSpeaker);
    }
  }, [realtimeCall.callActive, audioDevices.selectedSpeaker, realtimeCall]);

  // Track instructions view on mount
  useEffect(() => {
    void breadcrumb('interview_instructions_view');
  }, []);

  // LUM-72 in-call time warnings. While a call is active, poll every
  // 5s against the call-start timestamp; trip the T-5 then T-1 banner
  // exactly once each. Cleared when the call ends or the component
  // unmounts. The limit is the server-owned cap resolved from
  // /api/realtime/limits at start — no env-var drift.
  useEffect(() => {
    if (!realtimeCall.callActive || hardCapSec === null) return;
    if (callStartTimeRef.current === null) {
      callStartTimeRef.current = Date.now();
    }

    const limitSec = hardCapSec;
    const warn5AtMs = Math.max(0, (limitSec - 5 * 60) * 1000);
    const warn1AtMs = Math.max(0, (limitSec - 1 * 60) * 1000);

    const tick = () => {
      if (callStartTimeRef.current === null) return;
      const elapsedMs = Date.now() - callStartTimeRef.current;
      // Emit breadcrumbs from the ref-guarded effect body (NOT from
      // inside setState updaters, which React may call more than once
      // per commit in concurrent mode). Set state separately.
      if (elapsedMs >= warn1AtMs && !emittedWarningsRef.current.has('1min')) {
        emittedWarningsRef.current.add('1min');
        void breadcrumb('interview_time_warning_1min', {
          remaining_seconds_bucket: 60,
        });
        setTimeWarning('1min');
      } else if (
        elapsedMs >= warn5AtMs &&
        !emittedWarningsRef.current.has('5min')
      ) {
        emittedWarningsRef.current.add('5min');
        void breadcrumb('interview_time_warning_5min', {
          remaining_seconds_bucket: 300,
        });
        setTimeWarning('5min');
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [realtimeCall.callActive, hardCapSec]);

  // Start interview handler: call readiness → mic permission → session
  // create → resolve call limit → start call → branchy error setters
  // keyed on InterviewErrorCode.
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: start-call state machine
  const startInterview = useCallback(async () => {
    // Synchronous reentrancy guard — drop subsequent invocations
    // until the current chain completes.
    if (startInFlightRef.current) {
      logger.debug('startInterview: ignoring reentrant call');
      return;
    }
    startInFlightRef.current = true;

    if (!realtimeCall.ready) {
      setError('Voice system not initialized');
      setErrorCode(InterviewErrorCode.VOICE_NOT_INITIALIZED);
      startInFlightRef.current = false;
      return;
    }

    if (!session.canStartInterview) {
      setError('You have reached your interview limit');
      setErrorCode(InterviewErrorCode.INTERVIEW_LIMIT_REACHED);
      startInFlightRef.current = false;
      return;
    }

    // Pre-call mic check (LUM-82): opening the selected mic doubles as
    // the permission check, and Start stays blocked until the mic
    // carries a voice. A pass on this same device is not re-run.
    const micId = audioDevices.selectedMicrophone;
    const micOutcome =
      micCheck.status === 'passed' && micCheck.passedDeviceId === micId
        ? 'passed'
        : await micCheck.run(micId);
    if (micOutcome === 'denied') {
      let message = 'Microphone access is required to start the interview. ';
      if (isMac()) {
        message +=
          'On macOS, go to System Settings → Privacy & Security → Microphone.';
      } else if (isWindows()) {
        message +=
          'On Windows, go to Settings → Privacy → Microphone and ensure browser access is enabled.';
      } else if (isMobileDevice()) {
        message +=
          'In your device settings, enable microphone access for your browser app.';
      } else {
        message +=
          'Please check your browser settings and enable microphone access.';
      }
      setError(message);
      setErrorCode(InterviewErrorCode.VOICE_NOT_INITIALIZED);
      void breadcrumb('mic_permission_check', { granted: false });
      startInFlightRef.current = false;
      return;
    }
    void breadcrumb('mic_permission_check', { granted: true });
    if (micOutcome === 'silent') {
      // MicCheckPanel takes over: help text, picker, retry.
      startInFlightRef.current = false;
      return;
    }

    void breadcrumb('interview_start_click');
    setError(null);
    setErrorCode(null);
    setInterviewStarted(true);
    setEndedDueToSoftLimit(false);

    try {
      // Create session
      const sessionData = await session.createSession();
      if (!sessionData) {
        throw new Error('Failed to create session');
      }
      // Synchronously available to the call event callbacks before the
      // hook's React state commits (LUM-81 telemetry join).
      currentSessionIdRef.current = sessionData.id;

      // Resolve the server-owned hard cap before starting the call —
      // the relay enforces the same value (plus a short grace), so the
      // in-call countdown (LUM-72) ends before the server-side close.
      const limitsRes = await apiFetch('/api/realtime/limits');
      if (!limitsRes.ok) {
        throw new Error(
          `Could not reach the voice service to check call limits (HTTP ${limitsRes.status}). Please try again in a moment.`,
        );
      }
      const limitsBody = (await limitsRes.json()) as {
        max_duration_seconds?: number;
      };
      const resolvedCap = limitsBody.max_duration_seconds;
      if (typeof resolvedCap !== 'number' || resolvedCap <= 0) {
        throw new Error(
          'The voice service did not return a valid call duration limit. Please try again.',
        );
      }
      setHardCapSec(resolvedCap);
      void breadcrumb('interview_limit_resolved', {
        max_duration_sec: resolvedCap,
      });

      // Start the call — only decrement quota after it is confirmed
      const callId = await realtimeCall.startCall(sessionData.id);
      if (callId) {
        currentCallIdRef.current = callId;
      }

      // Call started successfully — now decrement the quota
      session.decrementRemainingInterviews();
    } catch (err) {
      logger.error('Failed to start interview', { error: err });
      const message =
        err instanceof Error ? err.message : 'Failed to start interview';
      setError(message);
      setInterviewStarted(false);
      void breadcrumb('interview_error', {
        code: 'start_failed',
        phase: 'start',
      });
    } finally {
      startInFlightRef.current = false;
    }
  }, [realtimeCall, session, micCheck, audioDevices.selectedMicrophone]);

  // End interview handler
  const endInterview = useCallback(async () => {
    void breadcrumb('interview_user_end_click');
    await realtimeCall.endCall();
    setInterviewStarted(false);
    if (interviewStarted) {
      setShowCompletionMessage(true);
    }
  }, [realtimeCall, interviewStarted]);

  // Start new interview handler
  const handleStartNewInterview = useCallback(() => {
    setShowCompletionMessage(false);
    setEndedDueToSoftLimit(false);
    setError(null);
    setErrorCode(null);
    setInterviewStarted(false);
    setCallEndSummary(null);
    setTimeWarning('none');
    setRegisterWarning(false);
    setHardCapSec(null);
    callStartTimeRef.current = null;
    emittedWarningsRef.current.clear();
    session.resetSession();
    realtimeCall.clearTranscripts();
    demoMode.resetDemoState();
    currentCallIdRef.current = null;
    currentSessionIdRef.current = null;
  }, [session, realtimeCall, demoMode]);

  // Recovery handlers
  const handleRetry = () => {
    setError(null);
    setErrorCode(null);
    startInterview();
  };

  const handleRefreshPage = () => window.location.reload();
  const clearError = () => {
    setError(null);
    setErrorCode(null);
  };
  // Switching mic from the mic check's help panel checks the new one
  // straight away (LUM-82).
  const selectMicrophoneAndCheck = (deviceId: string) => {
    audioDevices.selectMicrophone(deviceId);
    void micCheck.run(deviceId);
  };

  // Loading state
  if (session.loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  // No profile state
  if (!session.profile) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Profile Required"
        color="red"
      >
        Unable to load your profile. Please contact support.
      </Alert>
    );
  }

  const { profile } = session;
  // Last non-empty theme list: keeps the outline mounted across the
  // session swap at Start (themes transiently empty for ~300ms).
  const stableThemes =
    session.themes.length > 0 ? session.themes : lastThemesRef.current;
  lastThemesRef.current = stableThemes;
  const showAudioDeviceSelector =
    !inStage &&
    !showCompletionMessage &&
    !audioDevices.isMobile &&
    detailsConfirmed;

  return (
    <ErrorBoundary>
      <Stack gap="lg">
        {/* User Details Confirmation */}
        {!detailsConfirmed && (
          <DetailsConfirmationCard
            profile={profile}
            onConfirm={() => setDetailsConfirmed(true)}
          />
        )}

        {/* No interviews remaining message */}
        {!detailsConfirmed && session.remainingInterviews === 0 && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            variant="light"
          >
            <Text fw={500} mb="xs">
              No Interviews Remaining
            </Text>
            <Text size="sm">
              You have reached your interview limit. Please contact{' '}
              <a
                href="mailto:dialogue-help@luminarium.ai"
                style={{ color: 'var(--mantine-color-navy-7)' }}
              >
                dialogue-help@luminarium.ai
              </a>{' '}
              if you need to schedule additional interviews.
            </Text>
          </Alert>
        )}

        {/* Main Interview Section */}
        {detailsConfirmed && (
          <>
            {/* Workspace title row. In-call the serif headline shrinks
                to an eyebrow (LUM-185 Stage): the conversation, not the
                page furniture, is the subject while talking. */}
            <Stack gap="md">
              {/* One serif heading across every phase (owner feedback:
                  the in-call eyebrow read as a different page). The
                  badge is pre-call/wrap-up furniture only. */}
              <Group gap={14} align="baseline" wrap="wrap">
                <Title fz={{ base: 30, sm: 36 }} fw={500} lh={1.12}>
                  {JOURNEY_STEPS[journeyStep]}
                </Title>
                {!inStage &&
                  (showCompletionMessage && !error ? (
                    <Badge
                      variant="outline"
                      color="green.6"
                      styles={{
                        root: { background: 'rgba(105, 161, 92, 0.12)' },
                      }}
                    >
                      Saved
                    </Badge>
                  ) : (
                    renderRemainingBadge(session.remainingInterviews)
                  ))}
              </Group>

              {/* The Stage (LUM-185): in-call the Companion mark holds
                  the centre — nothing competes with it. The timer is
                  one quiet line, not a display number. */}
              {inStage && (
                <Stack
                  className="dd-phase-fade"
                  align="center"
                  justify="center"
                  gap={18}
                  mih="34vh"
                  py={20}
                >
                  <PresenceOrb
                    state={realtimeCall.agentState}
                    variant="stage"
                    connecting={!realtimeCall.callActive}
                  />
                  {/* Fixed-height slot: the timer line must not change
                      the centered stack's height when the call goes
                      live, or the mark hops (owner feedback). */}
                  <Box h={26} style={{ display: 'flex', alignItems: 'center' }}>
                    {realtimeCall.callActive && hardCapSec !== null && (
                      <Text
                        fz={12.5}
                        c="navy.6"
                        style={{
                          fontVariantNumeric: 'tabular-nums',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {formatElapsed(Math.max(0, hardCapSec - elapsedSec))}{' '}
                        remaining
                      </Text>
                    )}
                  </Box>
                  {/* In-call "we can hear you" meter (LUM-82) — fixed
                      slot for the same no-hop reason. */}
                  <Box h={20} style={{ display: 'flex', alignItems: 'center' }}>
                    {realtimeCall.callActive && (
                      <MicLevelIndicator
                        subscribe={realtimeCall.subscribeMicLevel}
                      />
                    )}
                  </Box>
                  {/* End control lives INSIDE the stage, centered under
                      the timer — field finding (Magda, 2 calls,
                      2026-07-27): the quiet bottom-right outline was
                      never found, even with the AI saying "you can end
                      the call"; both calls ended by closing the tab.
                      The user's eyes rest on the mark, so the exit
                      must sit in that same column. Fixed-height slot
                      for the same no-hop reason as the timer line;
                      still an outline — destructive is never filled
                      (owner rule). */}
                  <Box h={42} style={{ display: 'flex', alignItems: 'center' }}>
                    {realtimeCall.callActive && (
                      <Button
                        variant="outline"
                        color="red.7"
                        size="md"
                        leftSection={<IconPhoneOff size={18} />}
                        onClick={endInterview}
                      >
                        End interview
                      </Button>
                    )}
                  </Box>
                </Stack>
              )}

              {/* Mid-call session health (LUM-82): each banner clears
                  with its condition. */}
              <CallHealthBanners banners={callHealth.banners} />

              {/* Pre-call intro copy: belongs to the room before the
                  call — it steps aside once you're talking (LUM-185). */}
              {!inStage && !showCompletionMessage && (
                <Box className="dd-phase-fade">
                  <SafeMarkdown content={uxCopy.intro_card_text} size="sm" />
                </Box>
              )}

              {/* In-call time warnings (LUM-72). Shown only while
                      a call is active. T-5 is non-disruptive; T-1 is
                      prominent. Both persist until call ends. */}
              {realtimeCall.callActive && timeWarning === '5min' && (
                <Alert
                  icon={<IconClock size={16} />}
                  color="yellow"
                  variant="light"
                  title="5 minutes remaining"
                >
                  <Text size="sm">
                    Start wrapping up if you have more to share — we'll save
                    everything up to the time limit.
                  </Text>
                </Alert>
              )}
              {realtimeCall.callActive && timeWarning === '1min' && (
                <Alert
                  icon={<IconClockExclamation size={18} />}
                  color="yellow"
                  variant="filled"
                  title="Call ending soon"
                >
                  <Text size="sm" c="white">
                    Less than a minute left — your interview will be saved
                    automatically when the time is up.
                  </Text>
                </Alert>
              )}

              {/* LUM-81: call-id binding failed client-side. Non-
                      blocking — the interview keeps running and is
                      normally still recovered server-side, but the
                      user's history may lag. */}
              {registerWarning && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="yellow"
                  variant="light"
                  title="Heads up"
                >
                  <Text size="sm">
                    Your interview is being recorded as normal — but if it
                    doesn't appear in your history after the call, please
                    refresh the page.
                  </Text>
                </Alert>
              )}

              {/* Completion (LUM-185 harmonized): the wrap-up speaks the
                  same hairline-ledger language as the rest of the flow —
                  no alert chrome, no re-shown pre-call copy. */}
              {showCompletionMessage && !error && (
                <Box
                  className="dd-phase-fade"
                  style={{
                    borderTop: '1px solid rgba(29, 36, 50, 0.16)',
                    borderBottom: '1px solid rgba(29, 36, 50, 0.16)',
                    padding: '20px 2px',
                  }}
                >
                  <Stack gap="sm">
                    <Text
                      tt="uppercase"
                      fz={11}
                      fw={600}
                      c="navy.6"
                      style={{ letterSpacing: '0.16em' }}
                    >
                      {endedDueToSoftLimit
                        ? 'Your time is up'
                        : 'Interview complete'}
                    </Text>
                    {endedDueToSoftLimit && (
                      <SafeMarkdown
                        content={uxCopy.time_capped_text}
                        size="sm"
                      />
                    )}
                    {!endedDueToSoftLimit && uxCopy.completion && (
                      <>
                        <Text fz={17} fw={600} c="navy.8">
                          {uxCopy.completion.title}
                        </Text>
                        <SafeMarkdown
                          content={uxCopy.completion.body}
                          size="sm"
                        />
                        {uxCopy.completion.nextSteps.length > 0 && (
                          <CopyCellStrip
                            label="Next steps"
                            cells={uxCopy.completion.nextSteps}
                          />
                        )}
                      </>
                    )}
                    {!endedDueToSoftLimit && !uxCopy.completion && (
                      <SafeMarkdown
                        content={uxCopy.completion_text}
                        size="sm"
                      />
                    )}
                    {session.canStartInterview && (
                      <Button
                        size="sm"
                        variant="subtle"
                        color="navy"
                        w="fit-content"
                        px={0}
                        leftSection={<IconRefresh size={16} />}
                        onClick={handleStartNewInterview}
                      >
                        Start another interview
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Pre-call practicalities — leaves the moment Start is
                  pressed. Structured copy renders as three hairline cells;
                  a profile with only the timing blob keeps it (LUM-158). */}
              {!inStage &&
                !showCompletionMessage &&
                !error &&
                session.canStartInterview &&
                (uxCopy.practicalities ? (
                  <CopyCellStrip
                    label="Practicalities"
                    cells={[
                      uxCopy.practicalities.duration,
                      uxCopy.practicalities.environment,
                      uxCopy.practicalities.connection,
                    ]}
                  />
                ) : (
                  <Box
                    style={{
                      // Hairline practicalities strip (storyboard).
                      borderTop: '1px solid rgba(29, 36, 50, 0.16)',
                      borderBottom: '1px solid rgba(29, 36, 50, 0.16)',
                      padding: '14px 2px',
                    }}
                  >
                    <SafeMarkdown
                      content={uxCopy.interview_timing_text}
                      size="sm"
                      listIcon={
                        <img src={starOrange} alt="" style={{ height: 11 }} />
                      }
                    />
                  </Box>
                ))}

              {/* Post-call summary (demo mode): same hairline register.
                  With the LUM-182 substance gate a too-short call simply
                  never gets a summary — say so instead of spinning. */}
              {showCompletionMessage && !error && demoMode.demoMode && (
                <Box
                  className="dd-phase-fade"
                  style={{
                    borderBottom: '1px solid rgba(29, 36, 50, 0.16)',
                    padding: '4px 2px 20px',
                  }}
                >
                  <Stack gap="sm">
                    <Text
                      tt="uppercase"
                      fz={11}
                      fw={600}
                      c="navy.6"
                      style={{ letterSpacing: '0.16em' }}
                    >
                      What we heard
                    </Text>
                    {demoMode.demoSummary ? (
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                        {demoMode.demoSummary}
                      </Text>
                    ) : (
                      <Text size="sm" c="navy.6">
                        A short summary appears here a few seconds after the
                        call ends. Very short calls don't get one.
                      </Text>
                    )}
                  </Stack>
                </Box>
              )}
              {/* LUM-177 feedback — inline wrap-up section (a tester's
                  clean call produced no visible floating popup; in-flow
                  is viewport-proof and matches the hairline register). */}
              {showCompletionMessage && !error && feedbackCallId && (
                <Box className="dd-phase-fade">
                  <CallFeedbackCard
                    key={feedbackCallId}
                    callId={feedbackCallId}
                    onDismiss={() => setFeedbackCallId(null)}
                  />
                </Box>
              )}

              {/* Interview Limit Reached */}
              {!session.canStartInterview &&
                !interviewStarted &&
                !realtimeCall.connecting &&
                !realtimeCall.callActive && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    variant="light"
                  >
                    <Text fw={500} mb="xs">
                      Interview Limit Reached
                    </Text>
                    <Text size="sm">
                      Please contact{' '}
                      <a
                        href="mailto:dialogue-help@luminarium.ai"
                        style={{
                          color: 'var(--mantine-color-navy-7)',
                        }}
                      >
                        dialogue-help@luminarium.ai
                      </a>{' '}
                      to schedule additional interviews.
                    </Text>
                  </Alert>
                )}

              {/* Audio Device Selection — fixed-min-height wrapper: the
                  component swaps from a short loading alert to the full
                  two-select layout when devices enumerate; without the
                  reserve that swap popped the page (owner feedback). */}
              {showAudioDeviceSelector && (
                <Box mih={150}>
                  <AudioDeviceSelector
                    microphones={audioDevices.microphones}
                    speakers={audioDevices.speakers}
                    selectedMicrophone={audioDevices.selectedMicrophone}
                    selectedSpeaker={audioDevices.selectedSpeaker}
                    currentSpeakerLabel={audioDevices.currentSpeakerLabel}
                    supportsSetSinkId={audioDevices.supportsSetSinkId}
                    devicesLoaded={audioDevices.devicesLoaded}
                    showMacBluetoothTip={audioDevices.showMacBluetoothTip}
                    disabled={realtimeCall.connecting || interviewStarted}
                    hideMicrophone={micCheck.status === 'silent'}
                    onSelectMicrophone={audioDevices.selectMicrophone}
                    onSelectSpeaker={audioDevices.selectSpeaker}
                    onTestSpeaker={audioDevices.testSpeaker}
                    onDismissMacBluetoothTip={
                      audioDevices.dismissMacBluetoothTip
                    }
                  />
                </Box>
              )}

              {/* Pre-call mic check (LUM-82): live meter while it
                  listens; help, picker and retry when it heard nothing. */}
              {!inStage &&
                session.canStartInterview &&
                !error &&
                !showCompletionMessage && (
                  <MicCheckPanel
                    status={micCheck.status}
                    level={micCheck.level}
                    microphones={audioDevices.microphones}
                    selectedMicrophone={audioDevices.selectedMicrophone}
                    onSelectMicrophone={selectMicrophoneAndCheck}
                    onRetry={() =>
                      void micCheck.run(audioDevices.selectedMicrophone)
                    }
                  />
                )}

              {/* Start Interview Button */}
              {!inStage &&
                session.canStartInterview &&
                !error &&
                !showCompletionMessage && (
                  <Group justify="center">
                    <Button
                      onClick={startInterview}
                      loading={realtimeCall.connecting}
                      disabled={
                        !session.canStartInterview ||
                        interviewStarted ||
                        micCheck.status === 'checking' ||
                        micCheck.status === 'silent'
                      }
                      size="lg"
                      leftSection={<IconPhoneCall size={20} />}
                    >
                      {startButtonLabel({
                        interviewStarted,
                        connecting: realtimeCall.connecting,
                        checkingMic: micCheck.status === 'checking',
                        canStartInterview: session.canStartInterview,
                      })}
                    </Button>
                  </Group>
                )}
            </Stack>

            {/* Error Alert */}
            {!realtimeCall.callActive && error && (
              <InterviewErrorAlert
                error={error}
                errorCode={errorCode}
                canStartInterview={session.canStartInterview}
                onRetry={handleRetry}
                onRefreshPage={handleRefreshPage}
                onStartNewInterview={handleStartNewInterview}
                onClearError={clearError}
              />
            )}
          </>
        )}

        {/* Outline: wayfinding for before and during the call — not on
            the confirm-details step (it leaked there when stableThemes
            replaced the accidental sessionId gate) and not at wrap-up.
            stableThemes = last non-empty list, so the card survives the
            Start-click session swap (measured live). */}
        {detailsConfirmed &&
          !showCompletionMessage &&
          stableThemes.length > 0 && (
            <InterviewProgress
              themes={stableThemes}
              completedTopicIds={realtimeCall.markedTopicIds}
            />
          )}

        {/* Feedback for calls that end OUTSIDE the wrap-up (errors,
            edge paths): floating fallback so it is never unreachable. */}
        {!showCompletionMessage && feedbackCallId && (
          <CallFeedbackCard
            key={feedbackCallId}
            callId={feedbackCallId}
            onDismiss={() => setFeedbackCallId(null)}
            variant="floating"
          />
        )}
      </Stack>
    </ErrorBoundary>
  );
}

// ── Local presentational helpers (extracted from inline ternaries) ──

function formatElapsed(totalSec: number) {
  const m = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function renderRemainingBadge(remaining: number | null) {
  // Light-ground chips (the ivory workspace): hairline navy outline over a
  // light-blue tint, per the Observatory Split storyboard.
  const tint = { root: { background: 'rgba(163, 181, 209, 0.22)' } };
  if (remaining === null) return null;
  if (remaining === -1) {
    return (
      <Badge color="navy.7" variant="outline" styles={tint}>
        UNLIMITED
      </Badge>
    );
  }
  if (remaining === 0) {
    return (
      <Badge color="red.6" variant="outline">
        No interviews remaining
      </Badge>
    );
  }
  return (
    <Badge color="navy.7" variant="outline" styles={tint}>
      {remaining} interview{remaining !== 1 ? 's' : ''} remaining
    </Badge>
  );
}

function startButtonLabel(args: {
  interviewStarted: boolean;
  connecting: boolean;
  checkingMic: boolean;
  canStartInterview: boolean;
}): string {
  if (args.interviewStarted || args.connecting) return 'Starting...';
  if (args.checkingMic) return 'Checking microphone...';
  if (!args.canStartInterview) return 'Interview Limit Reached';
  return 'Start Interview';
}
